import { useCallback, useState } from 'react'
import toast from 'react-hot-toast'

import { olarkId } from '@fv/client-core'
import { SUPPORT_EMAIL, SUPPORT_PHONE } from '@fv/models'

import {
  expandOlarkBox,
  type LoadOlarkOptions,
  loadOlarkScript,
  type OlarkEmbedMode,
  resetOlarkLoadState,
} from './loadOlark.js'

export type UseLoadOlarkOptions = {
  mode?: OlarkEmbedMode
}

export type UseLoadOlarkResult = {
  expandOlarkBox: () => void
  isEnabled: boolean
  isLoading: boolean
  loadOlark: (options?: Omit<LoadOlarkOptions, 'mode'>) => Promise<void>
}

export const useLoadOlark = (
  hookOptions?: UseLoadOlarkOptions,
): UseLoadOlarkResult => {
  const [isLoading, setIsLoading] = useState(false)
  const mode = hookOptions?.mode ?? 'floating'

  const loadOlark = useCallback(
    async (options?: Omit<LoadOlarkOptions, 'mode'>) => {
      setIsLoading(true)
      try {
        await loadOlarkScript({
          ...options,
          mode,
          onReady: () => {
            options?.onReady?.()
            setIsLoading(false)
          },
        })
        if (!options?.onReady) {
          setIsLoading(false)
        }
      } catch {
        toast.error(
          `Unable to load chat. Please check your ad blocker settings and try again.  If the problem persists, please contact support at ${SUPPORT_EMAIL} or ${SUPPORT_PHONE}.`,
        )
        resetOlarkLoadState()
        setIsLoading(false)
      }
    },
    [mode],
  )

  return {
    expandOlarkBox,
    isEnabled: Boolean(olarkId),
    isLoading,
    loadOlark,
  }
}
